/**
 * @fileoverview Task recurrence section (MOR-696 Stage 3)
 *
 * Side-panel surface for turning a task into a recurring series and editing or
 * removing an existing series. Supports daily/weekly/monthly/yearly cadences,
 * weekly weekday selection, monthly by-date and positional ("last Friday of
 * every month") modes, and never / on-date / after-N-occurrences end rules.
 *
 * A recurring task occurrence (`event.recurrenceRuleId` set) can be detached
 * into a standalone task, and the series can be paused, edited, or deleted.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { Pause, Play, Repeat, Scissors, Trash2 } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import type { TaskRecurrenceFrequency } from "@cvx/tasks/taskRecurrenceRules/schema";

import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { extractErrorMessage } from "@/utils/errors";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ToggleGroup, ToggleGroupItem } from "@/ui/primitives/toggle-group";
import { DatePicker } from "@/ui/composed/inputs/date-picker";

import type { CalendarEventRow } from "@/calendar/eventAdapter";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const FREQUENCY_OPTIONS: Array<{
  value: TaskRecurrenceFrequency;
  label: string;
  unit: string;
}> = [
  { value: "daily", label: "Daily", unit: "day" },
  { value: "weekly", label: "Weekly", unit: "week" },
  { value: "monthly", label: "Monthly", unit: "month" },
  { value: "yearly", label: "Yearly", unit: "year" },
];
const SET_POS_OPTIONS = [
  { value: 1, label: "first" },
  { value: 2, label: "second" },
  { value: 3, label: "third" },
  { value: 4, label: "fourth" },
  { value: -1, label: "last" },
];

const END_MODES = ["never", "onDate", "afterCount"] as const;
type EndMode = (typeof END_MODES)[number];
function isEndMode(value: string): value is EndMode {
  return (END_MODES as readonly string[]).includes(value);
}

function isTaskRecurrenceFrequency(
  value: string,
): value is TaskRecurrenceFrequency {
  return FREQUENCY_OPTIONS.some((f) => f.value === value);
}

interface BuilderState {
  frequency: TaskRecurrenceFrequency;
  interval: number;
  byWeekday: number[];
  monthlyMode: "byMonthDay" | "positional";
  byMonthDay: number;
  bySetPos: number;
  positionalWeekday: number;
  startDate: string;
  endMode: EndMode;
  endDate: string;
  count: number;
}

function defaultBuilder(event: CalendarEventRow): BuilderState {
  const start = event.date;
  const weekday = new Date(`${start}T00:00:00Z`).getUTCDay();
  const monthDay = Number(start.slice(8, 10)) || 1;
  return {
    frequency: "weekly",
    interval: 1,
    byWeekday: [weekday],
    monthlyMode: "byMonthDay",
    byMonthDay: monthDay,
    bySetPos: 1,
    positionalWeekday: weekday,
    startDate: start,
    endMode: "never",
    endDate: "",
    count: 10,
  };
}

function builderFromRule(rule: Doc<"taskRecurrenceRules">): BuilderState {
  const start = rule.startDate;
  const weekday = new Date(`${start}T00:00:00Z`).getUTCDay();
  return {
    frequency: rule.frequency,
    interval: rule.interval,
    byWeekday:
      rule.byWeekday && rule.byWeekday.length > 0 ? rule.byWeekday : [weekday],
    monthlyMode: rule.bySetPos !== undefined ? "positional" : "byMonthDay",
    byMonthDay: rule.byMonthDay ?? (Number(start.slice(8, 10)) || 1),
    bySetPos: rule.bySetPos ?? 1,
    positionalWeekday:
      rule.bySetPos !== undefined && rule.byWeekday && rule.byWeekday.length > 0
        ? rule.byWeekday[0]
        : weekday,
    startDate: start,
    endMode: rule.endDate
      ? "onDate"
      : rule.count !== undefined
        ? "afterCount"
        : "never",
    endDate: rule.endDate ?? "",
    count: rule.count ?? 10,
  };
}

/** Human summary of a stored rule, e.g. "Every 2 weeks on Mon, Wed". */
function describeRule(rule: Doc<"taskRecurrenceRules">): string {
  const opt = FREQUENCY_OPTIONS.find((f) => f.value === rule.frequency);
  const unit = opt?.unit ?? rule.frequency;
  const base =
    rule.interval === 1 ? `Every ${unit}` : `Every ${rule.interval} ${unit}s`;
  if (rule.frequency === "weekly" && rule.byWeekday?.length) {
    const days = rule.byWeekday
      .map((d) => WEEKDAY_NAMES[d]?.slice(0, 3))
      .filter(Boolean)
      .join(", ");
    return `${base} on ${days}`;
  }
  if (rule.frequency === "monthly") {
    if (rule.bySetPos !== undefined && rule.byWeekday?.length) {
      const pos = SET_POS_OPTIONS.find((p) => p.value === rule.bySetPos)?.label;
      return `${base} on the ${pos} ${WEEKDAY_NAMES[rule.byWeekday[0]]}`;
    }
    if (rule.byMonthDay) return `${base} on day ${rule.byMonthDay}`;
  }
  return base;
}

export function TaskRecurrenceSection({ event }: { event: CalendarEventRow }) {
  const rules = useQuery(api.tasks.recurrence.listForJob, {
    jobId: event.jobId,
  });
  const createRule = useMutation(api.tasks.recurrence.create);
  const updateRule = useMutation(api.tasks.recurrence.update);
  const setPaused = useMutation(api.tasks.recurrence.setPaused);
  const removeRule = useMutation(api.tasks.recurrence.remove);
  const detachInstance = useMutation(api.tasks.recurrence.detachInstance);

  const rule = useMemo(
    () =>
      event.recurrenceRuleId
        ? ((rules ?? []).find((r) => r._id === event.recurrenceRuleId) ?? null)
        : null,
    [rules, event.recurrenceRuleId],
  );

  const [building, setBuilding] = useState(false);
  const [builder, setBuilder] = useState<BuilderState | null>(null);

  const startCreate = () => {
    setBuilder(defaultBuilder(event));
    setBuilding(true);
  };
  const startEdit = () => {
    if (!rule) return;
    setBuilder(builderFromRule(rule));
    setBuilding(true);
  };
  const cancel = () => {
    setBuilding(false);
    setBuilder(null);
  };

  const buildPayload = (b: BuilderState) => {
    const payload: {
      frequency: TaskRecurrenceFrequency;
      interval: number;
      startDate: string;
      byWeekday?: number[];
      bySetPos?: number;
      byMonthDay?: number;
      endDate?: string;
      count?: number;
    } = {
      frequency: b.frequency,
      interval: Math.max(1, b.interval),
      startDate: b.startDate,
    };
    if (b.frequency === "weekly" && b.byWeekday.length > 0) {
      payload.byWeekday = [...b.byWeekday].sort((x, y) => x - y);
    }
    if (b.frequency === "monthly") {
      if (b.monthlyMode === "positional") {
        payload.bySetPos = b.bySetPos;
        payload.byWeekday = [b.positionalWeekday];
      } else {
        payload.byMonthDay = b.byMonthDay;
      }
    }
    if (b.endMode === "onDate" && b.endDate) payload.endDate = b.endDate;
    if (b.endMode === "afterCount") payload.count = Math.max(1, b.count);
    return payload;
  };

  const submit = async () => {
    if (!builder) return;
    const payload = buildPayload(builder);
    try {
      if (rule) {
        await updateRule({
          ruleId: rule._id,
          patch: {
            frequency: payload.frequency,
            interval: payload.interval,
            startDate: payload.startDate,
            byWeekday: payload.byWeekday ?? null,
            bySetPos: payload.bySetPos ?? null,
            byMonthDay: payload.byMonthDay ?? null,
            endDate: payload.endDate ?? null,
            count: payload.count ?? null,
          },
          regenerateFuture: true,
        });
        toast.success("Recurrence updated.");
      } else {
        if (!event.persistedId) {
          toast.error("Save this task before making it recurring.");
          return;
        }
        await createRule({
          jobId: event.jobId,
          title: event.title,
          category: event.category,
          assigneeUserId: event.assigneeUserId ?? undefined,
          priority: event.priority ?? undefined,
          labelIds:
            event.labelIds.length > 0
              ? (event.labelIds as Array<Id<"taskLabels">>)
              : undefined,
          ...payload,
        });
        toast.success("Recurring series created.");
      }
      cancel();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save recurrence."));
    }
  };

  const guarded = async (fn: () => Promise<unknown>, fallback: string) => {
    try {
      await fn();
    } catch (e) {
      toast.error(extractErrorMessage(e, fallback));
    }
  };

  // ---- render ------------------------------------------------------------

  if (building && builder) {
    return (
      <RecurrenceBuilder
        builder={builder}
        setBuilder={setBuilder}
        onCancel={cancel}
        onSubmit={submit}
        editing={!!rule}
      />
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
        <Repeat className="size-3.5" />
        Recurrence
      </h4>

      {rule ? (
        <div className="space-y-2 rounded-md border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{describeRule(rule)}</span>
            {rule.pausedAt !== undefined && (
              <Badge color="amber" variant="soft" size="sm">
                Paused
              </Badge>
            )}
          </div>
          {/* Scope line: makes the bound explicit so an open-ended series
              never reads as "spawns infinite tasks". */}
          <p className="text-xs text-muted-foreground">
            {rule.endDate
              ? `Ends ${rule.endDate}.`
              : rule.count !== undefined
                ? `${rule.generatedCount ?? 0} of ${rule.count} occurrences created.`
                : `Schedules occurrences up to ${rule.generateAheadDays} days ahead — new ones appear automatically as time passes.`}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={startEdit}
            >
              Edit series
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() =>
                guarded(
                  () =>
                    setPaused({
                      ruleId: rule._id,
                      paused: rule.pausedAt === undefined,
                    }),
                  "Could not update the series.",
                )
              }
            >
              {rule.pausedAt === undefined ? (
                <>
                  <Pause className="size-3.5" /> Pause
                </>
              ) : (
                <>
                  <Play className="size-3.5" /> Resume
                </>
              )}
            </Button>
            {event.persistedId !== null && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => {
                  const taskId = event.persistedId;
                  if (!taskId) return;
                  void guarded(
                    () => detachInstance({ taskId }),
                    "Could not detach this occurrence.",
                  );
                }}
              >
                <Scissors className="size-3.5" /> Detach this one
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={() =>
                guarded(
                  () => removeRule({ ruleId: rule._id, deleteUpcoming: true }),
                  "Could not delete the series.",
                )
              }
            >
              <Trash2 className="size-3.5" /> Delete series
            </Button>
          </div>
        </div>
      ) : event.persistedId ? (
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={startCreate}
        >
          <Repeat className="size-3.5" /> Make recurring
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          Assign or restatus this item first to make it recurring.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Builder form
// ---------------------------------------------------------------------------

function RecurrenceBuilder({
  builder,
  setBuilder,
  onCancel,
  onSubmit,
  editing,
}: {
  builder: BuilderState;
  setBuilder: React.Dispatch<React.SetStateAction<BuilderState | null>>;
  onCancel: () => void;
  onSubmit: () => void;
  editing: boolean;
}) {
  const patch = (p: Partial<BuilderState>) =>
    setBuilder((prev) => (prev ? { ...prev, ...p } : prev));
  const unit =
    FREQUENCY_OPTIONS.find((f) => f.value === builder.frequency)?.unit ?? "";

  return (
    <div className="space-y-3 rounded-md border p-3">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Repeat className="size-3.5" />
        {editing ? "Edit recurrence" : "New recurrence"}
      </h4>

      {/* Frequency + interval */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Every</span>
        <Input
          type="number"
          min={1}
          value={builder.interval}
          onChange={(e) => patch({ interval: Number(e.target.value) || 1 })}
          className="h-8 w-16"
        />
        <Select
          value={builder.frequency}
          onValueChange={(v) => {
            if (isTaskRecurrenceFrequency(v)) patch({ frequency: v });
          }}
        >
          <SelectTrigger className="h-8 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {builder.interval === 1 ? f.unit : `${f.unit}s`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Weekly weekday picker */}
      {builder.frequency === "weekly" && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">On</Label>
          <ToggleGroup
            type="multiple"
            value={builder.byWeekday.map(String)}
            onValueChange={(vals) =>
              patch({ byWeekday: vals.map(Number).sort((a, b) => a - b) })
            }
            className="justify-start"
          >
            {WEEKDAYS.map((d, i) => (
              <ToggleGroupItem
                key={i}
                value={String(i)}
                className="size-8 p-0 text-xs"
                aria-label={WEEKDAY_NAMES[i]}
              >
                {d}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {/* Monthly mode */}
      {builder.frequency === "monthly" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="rec-monthday"
              checked={builder.monthlyMode === "byMonthDay"}
              onChange={() => patch({ monthlyMode: "byMonthDay" })}
            />
            <Label htmlFor="rec-monthday" className="text-sm">
              On day
            </Label>
            <Input
              type="number"
              min={1}
              max={31}
              value={builder.byMonthDay}
              onChange={(e) =>
                patch({ byMonthDay: Number(e.target.value) || 1 })
              }
              disabled={builder.monthlyMode !== "byMonthDay"}
              className="h-8 w-16"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="radio"
              id="rec-positional"
              checked={builder.monthlyMode === "positional"}
              onChange={() => patch({ monthlyMode: "positional" })}
            />
            <Label htmlFor="rec-positional" className="text-sm">
              On the
            </Label>
            <Select
              value={String(builder.bySetPos)}
              onValueChange={(v) => patch({ bySetPos: Number(v) })}
              disabled={builder.monthlyMode !== "positional"}
            >
              <SelectTrigger className="h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SET_POS_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={String(p.value)}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(builder.positionalWeekday)}
              onValueChange={(v) => patch({ positionalWeekday: Number(v) })}
              disabled={builder.monthlyMode !== "positional"}
            >
              <SelectTrigger className="h-8 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEKDAY_NAMES.map((name, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Start date */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Starts</Label>
        <DatePicker
          value={builder.startDate}
          onChange={(v) => patch({ startDate: v })}
        />
      </div>

      {/* End condition */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Ends</Label>
        <Select
          value={builder.endMode}
          onValueChange={(v) => {
            if (isEndMode(v)) patch({ endMode: v });
          }}
        >
          <SelectTrigger className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="never">Never</SelectItem>
            <SelectItem value="onDate">On date</SelectItem>
            <SelectItem value="afterCount">After N occurrences</SelectItem>
          </SelectContent>
        </Select>
        {builder.endMode === "onDate" && (
          <DatePicker
            value={builder.endDate}
            onChange={(v) => patch({ endDate: v })}
          />
        )}
        {builder.endMode === "afterCount" && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              value={builder.count}
              onChange={(e) => patch({ count: Number(e.target.value) || 1 })}
              className="h-8 w-20"
            />
            <span className="text-sm text-muted-foreground">occurrences</span>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Repeats every{" "}
        {builder.interval === 1 ? unit : `${builder.interval} ${unit}s`}.
        {builder.endMode === "never" &&
          " Tasks are created on a rolling 90-day horizon, not all at once — set an end date or occurrence cap to bound the series."}
      </p>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={onSubmit}>
          {editing ? "Save" : "Create"}
        </Button>
      </div>
    </div>
  );
}
